import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import useUser from '../hooks/useUser';
import './CategoryMaster.css';

const statusOptions = [
    { label: 'Active',   value: 'Active'   },
    { label: 'Inactive', value: 'Inactive' }
];

const emptyRow = () => ({ category_name: '', status: 'Active', effective_from: null, temp_id: Date.now() + Math.random() });

const CategoryDialog = ({ visible, categoryData, clients, allCategories, onHide, onSave }) => {
    const today = new Date();
    const isEdit = !!(categoryData && categoryData.category_master_id);

    const { userRole, userClient } = useUser();
    const role = userRole || localStorage.getItem('role') || '';
    const roleLower = role?.toLowerCase() || '';
    const isGspos = roleLower === 'gspos';
    const lockedClientId = userClient || localStorage.getItem('clientid') || '';

    const [selectedClientId, setSelectedClientId] = useState('');
    const [items, setItems] = useState([emptyRow()]);

    useEffect(() => {
        if (!visible) return;
        if (isEdit) {
            setSelectedClientId(categoryData.client_id);
            setItems([{
                ...categoryData,
                effective_from: categoryData.effective_from ? new Date(categoryData.effective_from) : null
            }]);
        } else {
            setSelectedClientId(isGspos ? '' : lockedClientId);
            setItems([emptyRow()]);
        }
    }, [visible, categoryData, isGspos, lockedClientId]);

    const addRow    = () => setItems((prev) => [...prev, emptyRow()]);
    const removeRow = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
    const updateRow = (idx, field, val) =>
        setItems((prev) => prev.map((it, i) => i === idx ? { ...it, [field]: val } : it));

    const canSave = selectedClientId && items.every((it) => it.category_name?.trim());

    const dialogHeader = (
        <div className="sub-dialog-header">
            <span className="sub-dialog-header-icon">
                <i className="pi pi-tags" />
            </span>
            <div>
                <div className="sub-dialog-header-text">
                    {isEdit ? 'Edit Category' : 'Add Category'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                    {isEdit ? 'Update the category details below' : 'Fill in details and add multiple rows at once'}
                </div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-between align-items-center">
            {!isEdit ? (
                <Button
                    label="Add Row"
                    icon="pi pi-plus"
                    className="p-button-text p-button-sm"
                    style={{ color: '#7c3aed', fontWeight: 600 }}
                    onClick={addRow}
                    disabled={!selectedClientId}
                />
            ) : <span />}
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={onHide} />
                <Button
                    label={isEdit ? 'Update' : 'Save Changes'}
                    icon="pi pi-check"
                    className="sub-dialog-save"
                    onClick={() => onSave(items.map((it) => ({ ...it, client_id: selectedClientId })))}
                    disabled={!canSave}
                />
            </div>
        </div>
    );

    return (
        <Dialog
            visible={visible}
            header={dialogHeader}
            footer={dialogFooter}
            onHide={onHide}
            modal
            position="center"
            style={{ width: '700px' }}
            draggable={false}
            resizable={false}
            className="sub-master-dialog p-fluid"
        >
            {/* Client selector */}
            <div className="sub-field-group mb-3">
                <label className="sub-field-label">
                    <i className="pi pi-building sub-field-label-icon" />
                    Client
                </label>
                <Dropdown
                    value={selectedClientId}
                    options={clients}
                    optionValue="client_id"
                    optionLabel="client_name"
                    onChange={(e) => setSelectedClientId(e.value)}
                    placeholder="Select a client..."
                    filter
                    showClear={!isEdit && isGspos}
                    disabled={isEdit || !isGspos}
                    className="w-full"
                />
            </div>

            {/* Column header labels */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 140px 40px', gap: '0.75rem', padding: '0 0.1rem 0.4rem', marginBottom: '0.25rem' }}>
                <span className="sub-field-label"><i className="pi pi-tag sub-field-label-icon" /> Category Name *</span>
                <span className="sub-field-label"><i className="pi pi-calendar sub-field-label-icon" /> Effective From</span>
                <span className="sub-field-label"><i className="pi pi-circle sub-field-label-icon" /> Status</span>
                <span />
            </div>

            {/* Rows */}
            <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '2px' }}>
                {items.map((item, idx) => (
                    <div
                        key={item.temp_id || idx}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 160px 140px 40px',
                            gap: '0.75rem',
                            alignItems: 'end',
                            background: '#fff',
                            border: '1.5px solid #e8e9f7',
                            borderRadius: 10,
                            padding: '0.85rem 1rem',
                            marginBottom: '0.6rem',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                            transition: 'box-shadow 0.2s, border-color 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 3px 10px rgba(99,102,241,0.14)'; e.currentTarget.style.borderColor = '#6366f1'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e8e9f7'; }}
                    >
                        <InputText
                            value={item.category_name}
                            onChange={(e) => updateRow(idx, 'category_name', e.target.value)}
                            placeholder="e.g. Beverages"
                            disabled={!selectedClientId}
                            style={{ fontSize: '0.875rem', borderColor: item.category_name?.trim() ? '#dde1f0' : '#fca5a5', borderRadius: 8 }}
                        />
                        <Calendar
                            value={item.effective_from}
                            onChange={(e) => updateRow(idx, 'effective_from', e.value)}
                            minDate={today}
                            showIcon
                            placeholder="Pick date"
                            dateFormat="dd-mm-yy"
                            style={{ fontSize: '0.875rem' }}
                        />
                        <Dropdown
                            value={item.status}
                            options={statusOptions}
                            onChange={(e) => updateRow(idx, 'status', e.value)}
                            style={{ fontSize: '0.875rem' }}
                        />
                        <Button
                            icon="pi pi-trash"
                            className="p-button-rounded p-button-text p-button-danger p-button-sm"
                            onClick={() => removeRow(idx)}
                            disabled={items.length === 1}
                            tooltip="Remove"
                            tooltipOptions={{ position: 'top' }}
                            style={{ width: 32, height: 32 }}
                        />
                    </div>
                ))}
            </div>

            {!selectedClientId && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', padding: '0.6rem 0.85rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                    <i className="pi pi-info-circle" style={{ color: '#3b82f6', fontSize: '0.9rem' }} />
                    <span style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 500 }}>Please select a client to enable category entry</span>
                </div>
            )}
        </Dialog>
    );
};

export default CategoryDialog;
